import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { sbq, throwOnError } from '../../shared/utils/supabaseQuery';
import { applyOwnerFields } from '../../shared/utils/applyOwnerFields';
import { getDefaultOverviewItems } from '../../shared/utils/contentItems';
import {
  placementToAnchorPair,
  reindexLessonBlocks,
  reorderByPlacement,
  type ReorderPlacement,
} from '../../shared/utils/reorder';
import { resourcesApi } from '../resources/resourcesApi';

import type {
  Lesson,
  CreateLessonRequest,
  UpdateLessonRequest,
  LessonContent,
} from '@/shared/types/lesson';

type MoveLessonOverviewItemArgs = {
  lessonId: string;
  itemId: string;
  placement: ReorderPlacement;
};

type MoveLessonBlockArgs = {
  lessonId: string;
  blockId: string;
  placement: ReorderPlacement;
};

function applyOptimisticLessonReorder(
  draft: Lesson[],
  lessonId: string,
  placement: ReorderPlacement
) {
  const reordered = reorderByPlacement(draft, lessonId, placement);
  reordered.forEach((lesson, index) => {
    lesson.position = (index + 1) * 1000;
  });
  draft.splice(0, draft.length, ...reordered);
}

export const lessonsApi = createApi({
  reducerPath: 'lessonsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Lesson'],
  endpoints: (builder) => ({
    // Get a single lesson by ID
    getLessonById: builder.query<Lesson, string>({
      queryFn: sbq(async (sb, lessonId) => {
        const data = throwOnError(
          await sb
            .from('lessons')
            .select('*, owner:users(name, avatar_url)')
            .eq('id', lessonId)
            .single()
        );

        // Attach owner_name for frontend compatibility
        return {
          ...applyOwnerFields(data as any),
          content: (data as any)?.content ?? {},
        } as Lesson;
      }, 'Lesson'),
      providesTags: (result, _error, lessonId) =>
        result ? [{ type: 'Lesson', id: lessonId }] : [],
    }),

    // Get lessons for a specific unit
    getLessonsByUnitId: builder.query<Lesson[], string>({
      queryFn: sbq(async (sb, unitId) => {
        const data = throwOnError(
          await sb
            .from('lessons')
            .select('*, owner:users(name, avatar_url)')
            .eq('unit_id', unitId)
            .eq('is_deleted', false)
            .order('position', { ascending: true })
        );

        // Attach owner_name for frontend compatibility
        return (data || []).map((lesson: any) => ({
          ...applyOwnerFields(lesson),
          content: lesson?.content ?? {},
        }));
      }, 'Lessons'),
      providesTags: ['Lesson'],
    }),

    // Get lessons owned by the current user
    getMyLessons: builder.query<Lesson[], string>({
      queryFn: sbq(async (sb, userId) => {
        const data = throwOnError(
          await sb
            .from('lessons')
            .select('*, owner:users(name, avatar_url), unit:units(title)')
            .eq('owner_id', userId)
            .eq('is_deleted', false)
            .order('updated_at', { ascending: false })
        );

        // Attach owner_name and unit_title for frontend compatibility
        return (data || []).map((lesson: any) => ({
          ...applyOwnerFields(lesson),
          unit_title: lesson?.unit?.title || '',
          content: lesson?.content ?? {},
        }));
      }, 'Lessons'),
      providesTags: ['Lesson'],
    }),

    // Create a new lesson
    createLesson: builder.mutation<Lesson, CreateLessonRequest>({
      queryFn: sbq(async (sb, lessonData) => {
        const now = new Date().toISOString();

        // Seed the default overview scaffold only when the caller didn't supply
        // its own content (e.g. the AI/import paths may pass content explicitly).
        const content: LessonContent = lessonData.content ?? {
          schemaVersion: '1',
          overview: getDefaultOverviewItems('lesson'),
          blocks: [],
        };

        const newLesson = {
          ...lessonData,
          content,
          created_at: now,
          updated_at: now,
        };

        const result = throwOnError(await sb.from('lessons').insert([newLesson]).select().single());

        return result as Lesson;
      }, 'Lesson'),
      invalidatesTags: ['Lesson'],
    }),

    // Update a lesson
    updateLesson: builder.mutation<Lesson, UpdateLessonRequest>({
      queryFn: sbq(async (sb, updateData) => {
        const { id, ...fields } = updateData;
        if (!id) throw new Error('Lesson ID is required');

        const now = new Date().toISOString();
        const updateFields = { ...fields, updated_at: now };

        const result = throwOnError(
          await sb.from('lessons').update(updateFields).eq('id', id).select().single()
        );

        return result as Lesson;
      }, 'Lesson'),
      invalidatesTags: ['Lesson'],
    }),

    // Update lesson content (specifically for blocks and rich content)
    updateLessonContent: builder.mutation<Lesson, { id: string; content: LessonContent }>({
      queryFn: sbq(async (sb, { id, content }) => {
        if (!id) throw new Error('Lesson ID is required');

        const now = new Date().toISOString();

        const result = throwOnError(
          await sb
            .from('lessons')
            .update({ content, updated_at: now })
            .eq('id', id)
            .select()
            .single()
        );

        return result as Lesson;
      }, 'Lesson'),
      invalidatesTags: ['Lesson'],
    }),

    // Delete a lesson
    deleteLesson: builder.mutation<void, string>({
      queryFn: sbq(async (sb, lessonId) => {
        if (!lessonId) throw new Error('Lesson ID is required');

        const { error } = await sb.rpc('soft_delete_lesson', { p_lesson_id: lessonId });
        if (error) throw error;

        return undefined;
      }, 'Lesson'),
      invalidatesTags: ['Lesson'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(resourcesApi.util.invalidateTags(['Resource', 'ResourceList']));
        } catch {
          // delete failed; nothing to invalidate
        }
      },
    }),

    // Move a lesson to a new position
    moveLesson: builder.mutation<
      void,
      { lessonId: string; unitId: string; placement: ReorderPlacement }
    >({
      queryFn: sbq(async (sb, { lessonId, placement }) => {
        const { beforeId, afterId } = placementToAnchorPair(placement);
        const { error } = await sb.rpc('move_lesson', {
          p_id: lessonId,
          p_before: beforeId,
          p_after: afterId,
        });
        if (error) throw error;
        return undefined;
      }, 'Lesson'),
      invalidatesTags: ['Lesson'],
      async onQueryStarted({ lessonId, unitId, placement }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          lessonsApi.util.updateQueryData('getLessonsByUnitId', unitId, (draft) => {
            applyOptimisticLessonReorder(draft, lessonId, placement);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    moveLessonOverviewItem: builder.mutation<void, MoveLessonOverviewItemArgs>({
      queryFn: sbq(async (sb, { lessonId, itemId, placement }) => {
        const { error } = await sb.rpc('move_lesson_overview_item', {
          p_lesson_id: lessonId,
          p_item_id: itemId,
          p_position: placement.position,
          p_anchor_id: placement.anchorId,
        });
        if (error) throw error;
        return undefined;
      }, 'Lesson'),
      invalidatesTags: ['Lesson'],
      async onQueryStarted({ lessonId, itemId, placement }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          lessonsApi.util.updateQueryData('getLessonById', lessonId, (draft) => {
            const content = draft.content ?? { schemaVersion: '1' };
            draft.content = {
              ...content,
              schemaVersion: '1',
              overview: reorderByPlacement(content.overview ?? [], itemId, placement),
            };
            draft.updated_at = new Date().toISOString();
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    moveLessonBlock: builder.mutation<void, MoveLessonBlockArgs>({
      queryFn: sbq(async (sb, { lessonId, blockId, placement }) => {
        const { error } = await sb.rpc('move_lesson_block', {
          p_lesson_id: lessonId,
          p_block_id: blockId,
          p_position: placement.position,
          p_anchor_id: placement.anchorId,
        });
        if (error) throw error;
        return undefined;
      }, 'Lesson'),
      invalidatesTags: ['Lesson'],
      async onQueryStarted({ lessonId, blockId, placement }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          lessonsApi.util.updateQueryData('getLessonById', lessonId, (draft) => {
            const content = draft.content ?? { schemaVersion: '1' };
            const reorderedBlocks = reorderByPlacement(content.blocks ?? [], blockId, placement);
            draft.content = {
              ...content,
              schemaVersion: '1',
              blocks: reindexLessonBlocks(reorderedBlocks),
            };
            draft.updated_at = new Date().toISOString();
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetLessonByIdQuery,
  useGetLessonsByUnitIdQuery,
  useGetMyLessonsQuery,
  useCreateLessonMutation,
  useUpdateLessonMutation,
  useUpdateLessonContentMutation,
  useDeleteLessonMutation,
  useMoveLessonMutation,
  useMoveLessonOverviewItemMutation,
  useMoveLessonBlockMutation,
} = lessonsApi;
